// src/pages/auth/SignInPage.tsx
//
// Cool-Linen sign-in page that replaces the old LoginPage. Matches the mockup at
// bizguard-backend/docs/mockups/sign-in-mockup.html.
//
// Two branches in this component:
//   - Default : email + password form, plus disabled Google/Apple SSO buttons.
//   - mfa     : 6-digit TOTP prompt for users who enabled 2FA (optional, opt-in).
//
// "Keep me signed in" was intentionally removed from this redesign per the Phase 2
// plan — see bizguard-backend/docs/PLAN_SIGNUP_REDESIGN.md.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';
import { ArrowRight, Eye, EyeOff, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useGoogleSignIn, type GoogleSignInError } from '@/hooks/useGoogleSignIn';
import { useAppleSignIn, type AppleSignInError } from '@/hooks/useAppleSignIn';
import zaminoEmblem from '@/assets/zamino-emblem.png';
import { consumeIntendedRoute } from '@/utils/intendedRoute';
import { loadGoogleAdsTag } from '@/utils/googleAds';
import { consumeSessionEndNotice } from '@/services/session';
import type { LoginForm } from '@/types';

/**
 * Map every OAuth (Google or Apple) failure to a user-facing toast.
 * Cancellation is silent — that's just the user changing their mind.
 *
 * The collision branch (`collision_with_existing_account`) is generic across
 * providers because the backend already personalises the message based on
 * which provider the existing account uses.
 */
function handleOAuthError(provider: 'google' | 'apple', err: unknown): void {
  const reason = (err as Partial<GoogleSignInError | AppleSignInError> | null)?.reason;
  const providerLabel = provider === 'google' ? 'Google' : 'Apple';

  if (reason === 'cancelled') return;
  if (reason === 'unavailable') {
    toast.error(
      `Couldn't open ${providerLabel} sign-in. Check your popup blocker and try again.`,
    );
    return;
  }
  if (reason === 'not_configured') {
    toast.error(`${providerLabel} sign-in is not configured on this server.`);
    return;
  }

  const axiosErr = err as {
    response?: {
      status?: number;
      data?: { code?: string; error?: string; existingAuthProvider?: string };
    };
  } | null;
  const code = axiosErr?.response?.data?.code;
  const serverMessage = axiosErr?.response?.data?.error;

  if (code === 'collision_with_existing_account' && serverMessage) {
    // Backend tailors the message to the existing account's auth_provider
    // (e.g. "Sign in with Google to continue"). Surface it verbatim — that's
    // exactly the action the user needs to take.
    toast.error(serverMessage);
    return;
  }
  if (code === 'google_not_configured' || code === 'apple_not_configured') {
    toast.error(`${providerLabel} sign-in is not configured on this server.`);
    return;
  }
  if (code === 'bad_token' || code === 'missing_email') {
    toast.error(`Couldn't verify your ${providerLabel} account. Please try again.`);
    return;
  }
  if (code === 'account_inactive') {
    toast.error('Your account is inactive. Contact support to restore access.');
    return;
  }
  toast.error(`${providerLabel} sign-in failed. Please try again.`);
}

const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const SignInPage: React.FC = () => {
  const { login, signInWithGoogle, signInWithApple } = useAuth();
  const navigate = useNavigate();
  const google = useGoogleSignIn();
  const apple = useAppleSignIn();

  // Auth routes are the only place the app loads an advertising tag: a paid
  // visitor can start an OAuth signup from here, and the conversion needs gtag
  // present when it fires. See CAMPAIGN_TRACKING_SPEC.md §6.2.
  useEffect(() => {
    loadGoogleAdsTag();
  }, []);

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [appleSubmitting, setAppleSubmitting] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [credentials, setCredentials] = useState<LoginForm | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState<string | null>(null);

  /** Why the last session ended, if it ended on its own. Read once per page
   *  load and shown above the form — the explanation belongs on the screen the
   *  user was sent to, not in a toast they may never look at. */
  const [sessionEndNotice] = useState(consumeSessionEndNotice);

  /** Deep links (e.g. the OAuth consent page) stash their URL before bouncing
   *  here — signing in returns the user there instead of /home. */
  const navigateAfterSignIn = () => {
    navigate(consumeIntendedRoute() ?? '/home', { replace: true });
  };

  /**
   * Click handler for the Google button. New users land on the sign-up wizard's
   * OAuth-profile step (so they fill in their business name without re-doing
   * the popup); existing users go straight to /home.
   */
  const onGoogleClick = async () => {
    if (!google.configured) {
      toast('Google sign-in is not configured on this server.');
      return;
    }
    try {
      setGoogleSubmitting(true);
      const idToken = await google.signIn();
      const result = await signInWithGoogle(idToken);
      if (result.kind === 'signed_in') {
        navigateAfterSignIn();
        return;
      }
      navigate('/signup', { state: { oauthSignup: result.payload } });
    } catch (err: unknown) {
      handleOAuthError('google', err);
    } finally {
      setGoogleSubmitting(false);
    }
  };

  /** Same routing as Google; the Apple SDK additionally returns the user's
   *  name on first sign-in only (forwarded as-is to the backend). */
  const onAppleClick = async () => {
    if (!apple.configured) {
      toast('Apple sign-in is not configured on this server.');
      return;
    }
    try {
      setAppleSubmitting(true);
      const { idToken, name } = await apple.signIn();
      const result = await signInWithApple({ idToken, name });
      if (result.kind === 'signed_in') {
        navigateAfterSignIn();
        return;
      }
      navigate('/signup', { state: { oauthSignup: result.payload } });
    } catch (err: unknown) {
      handleOAuthError('apple', err);
    } finally {
      setAppleSubmitting(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      setSubmitting(true);
      await login(data);
      navigateAfterSignIn();
    } catch (err) {
      const flags = err as { mfaRequired?: boolean } | null;
      if (flags?.mfaRequired) {
        setCredentials({ email: data.email, password: data.password });
        setMfaStep(true);
        return;
      }
      const status = err instanceof AxiosError ? err.response?.status : undefined;
      if (status === 423) {
        setError('email', {
          message: 'Account locked. Check your email for an unlock link.',
        });
      } else if (status === 401) {
        setError('email', { message: 'Invalid email or password' });
        setError('password', { message: 'Invalid email or password' });
      } else {
        setError('email', { message: 'Sign-in failed. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!credentials || totpCode.length !== 6) return;
    try {
      setSubmitting(true);
      setTotpError(null);
      await login({ ...credentials, totpCode });
      navigateAfterSignIn();
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 401) {
        setTotpCode('');
        setTotpError("That code didn't match. Try again.");
      } else {
        setTotpError('Could not verify the code. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Branch: TOTP prompt ----------------------------------------------
  if (mfaStep) {
    return (
      <div className="auth-surface">
        <div className="auth-bg-glow" aria-hidden="true" />
        <div className="auth-frame">
          <div className="auth-brand">
            <Link to="/" aria-label="Zamino home">
              <img src={zaminoEmblem} alt="Zamino" />
            </Link>
          </div>

          <h1 className="auth-headline">
            Two-factor <span className="accent">code.</span>
          </h1>
          <p className="auth-sub">Enter the 6-digit code from your authenticator app.</p>

          <form onSubmit={onMfaSubmit} style={{ marginTop: 32 }}>
            <label className="auth-label" htmlFor="totp">
              Authentication code
            </label>
            <input
              id="totp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={totpCode}
              onChange={(e) => {
                setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                if (totpError) setTotpError(null);
              }}
              placeholder="------"
              className={`auth-otp-input ${totpError ? 'is-invalid' : ''}`}
              autoFocus
              aria-invalid={!!totpError}
            />
            {totpError && (
              <p className="auth-error-text" role="alert">
                {totpError}
              </p>
            )}

            <button
              type="submit"
              className="auth-pill auth-pill-primary"
              style={{ marginTop: 20 }}
              disabled={totpCode.length !== 6 || submitting}
            >
              {submitting ? 'Verifying…' : 'Verify and sign in'}
              {!submitting && <ArrowRight size={14} strokeWidth={2.5} />}
            </button>

            <div
              style={{
                marginTop: 20,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 13,
                color: 'var(--auth-text-muted)',
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setMfaStep(false);
                  setTotpCode('');
                  setTotpError(null);
                  setCredentials(null);
                }}
                className="auth-link-subtle"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Back to sign in
              </button>
              <Link to="/mfa-reset-request" className="auth-link-subtle">
                Lost your authenticator?
              </Link>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ----- Branch: default email + password form ----------------------------
  return (
    <div className="auth-surface">
      <div className="auth-bg-glow" aria-hidden="true" />
      <div className="auth-frame">
        <div className="auth-brand">
          <Link to="/" aria-label="Zamino home">
            <img src={zaminoEmblem} alt="Zamino" />
          </Link>
        </div>

        <h1 className="auth-headline">
          {sessionEndNotice ? (
            <>Welcome back to <span className="accent">Zamino.</span></>
          ) : (
            <>Sign in to <span className="accent">Zamino.</span></>
          )}
        </h1>
        <p className="auth-sub">Pick up right where your data left off.</p>

        {sessionEndNotice && (
          <div
            className={`auth-notice is-${sessionEndNotice.tone}`}
            role={sessionEndNotice.tone === 'alert' ? 'alert' : 'status'}
          >
            <span className="auth-notice-pip" aria-hidden="true" />
            <span>{sessionEndNotice.message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: sessionEndNotice ? 28 : 40 }}>
          <div>
            <label className="auth-label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              className={`auth-field ${errors.email ? 'is-invalid' : ''}`}
              {...register('email')}
              autoFocus
            />
            {errors.email?.message && (
              <p className="auth-error-text" role="alert">
                {errors.email.message}
              </p>
            )}
          </div>

          <div style={{ marginTop: 16 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: 6,
              }}
            >
              <label className="auth-label" htmlFor="password" style={{ marginBottom: 0 }}>
                Password
              </label>
              <Link to="/forgot-password" className="auth-link-subtle">
                Forgot?
              </Link>
            </div>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter your password"
                className={`auth-field auth-field-with-icon ${errors.password ? 'is-invalid' : ''}`}
                {...register('password')}
              />
              <button
                type="button"
                className="auth-icon-btn"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password?.message && (
              <p className="auth-error-text" role="alert">
                {errors.password.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="auth-pill auth-pill-primary"
            style={{ marginTop: 24 }}
            disabled={submitting}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
            {!submitting && <ArrowRight size={14} strokeWidth={2.5} />}
          </button>
        </form>

        <div className="auth-divider" style={{ marginTop: 28 }}>
          or
        </div>

        <div style={{ marginTop: 28, display: 'grid', gap: 12 }}>
          <button
            type="button"
            className="auth-pill auth-pill-google"
            disabled={!google.configured || !google.ready || googleSubmitting}
            aria-disabled={!google.configured || !google.ready || googleSubmitting}
            onClick={onGoogleClick}
            title={
              !google.configured
                ? 'Google sign-in is not configured'
                : !google.ready
                  ? 'Google sign-in is loading'
                  : undefined
            }
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
              />
              <path
                fill="#34A853"
                d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
              />
              <path
                fill="#FBBC05"
                d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.333z"
              />
              <path
                fill="#EA4335"
                d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.294C4.672 5.166 6.656 3.58 9 3.58z"
              />
            </svg>
            <span>{googleSubmitting ? 'Signing in…' : 'Continue with Google'}</span>
          </button>

          <button
            type="button"
            className="auth-pill auth-pill-apple"
            disabled={!apple.configured || !apple.ready || appleSubmitting}
            aria-disabled={!apple.configured || !apple.ready || appleSubmitting}
            onClick={onAppleClick}
            title={
              !apple.configured
                ? 'Apple sign-in is not configured'
                : !apple.ready
                  ? 'Apple sign-in is loading'
                  : undefined
            }
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.05 12.04c-.03-2.84 2.32-4.2 2.43-4.27-1.32-1.94-3.39-2.21-4.12-2.24-1.76-.18-3.43 1.04-4.32 1.04-.9 0-2.27-1.01-3.74-.98-1.92.03-3.69 1.12-4.68 2.84-2 3.46-.51 8.59 1.43 11.4.95 1.38 2.07 2.92 3.55 2.86 1.43-.06 1.97-.92 3.7-.92 1.72 0 2.21.92 3.71.89 1.54-.03 2.51-1.39 3.43-2.78 1.09-1.59 1.53-3.13 1.55-3.21-.03-.01-2.97-1.14-3-4.53M14.32 3.84c.78-.95 1.31-2.27 1.16-3.59-1.13.05-2.5.76-3.31 1.71-.72.84-1.36 2.18-1.19 3.47 1.26.1 2.55-.64 3.34-1.59" />
            </svg>
            <span>{appleSubmitting ? 'Signing in…' : 'Continue with Apple'}</span>
          </button>
        </div>

        {/* Shown on sign-in too because the Google/Apple buttons create an account
            for first-time users — this is the only consent surface on that path. */}
        <p className="auth-micro" style={{ marginTop: 24, textAlign: 'center' }}>
          By continuing, you agree to Zamino&rsquo;s{' '}
          <a href="https://zamino.com/terms" target="_blank" rel="noreferrer">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="https://zamino.com/privacy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </p>

        <p
          className="auth-micro"
          style={{
            marginTop: 20,
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <Shield size={12} aria-hidden="true" />
          <span>Encrypted, access-controlled, and audited.</span>
        </p>

        <p style={{ marginTop: 12, textAlign: 'center', fontSize: 12, color: 'var(--auth-text-faint)' }}>
          Lost access to your authenticator?{' '}
          <Link to="/mfa-reset-request" className="auth-link-subtle">
            Reset MFA
          </Link>
        </p>

        <p style={{ marginTop: 40, textAlign: 'center', fontSize: 14, color: 'var(--auth-text-muted)' }}>
          Don&rsquo;t have an account?{' '}
          <Link to="/signup" className="auth-link">
            Sign up for free
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignInPage;
